import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Globe,
  Wifi,
  Clock,
  Wallet,
  Utensils,
  ShieldCheck,
  ChevronRight,
  QrCode,
  MapPin,
  Sparkles,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1200&q=80";

const QRLanding = () => {
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const hotelName = params.get("hotel");

  useEffect(() => {
    api
      .get("/restaurants", { params: { featured: true } })
      .then((r) => setRestaurants(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  // Auto-detect browser language on first visit (only if user hasn't set one manually)
  useEffect(() => {
    if (!localStorage.getItem("ce_lang")) {
      const b = (navigator.language || "en").slice(0, 2).toLowerCase();
      if (["en", "it", "es"].includes(b)) setLang(b);
    }
  }, [setLang]);

  const langNames = { en: "EN", it: "IT", es: "ES" };

  const perks = [
    { icon: MapPin, key: "qrPerk1" },
    { icon: Clock, key: "qrPerk2" },
    { icon: Wallet, key: "qrPerk3" },
    { icon: ShieldCheck, key: "qrPerk4" },
  ];

  return (
    <div className="min-h-screen bg-navy text-white overflow-hidden">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 pt-5">
        <Link to="/" className="flex items-center gap-2" data-testid="qr-logo">
          <div className="w-9 h-9 rounded-xl bg-coral flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-coral/30">
            CE
          </div>
          <span className="font-heading font-extrabold tracking-tight text-sm">
            CALETA EATS
          </span>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full h-9 px-3 bg-white/10 backdrop-blur-md text-white hover:bg-white/20"
              data-testid="qr-lang-switcher"
            >
              <Globe className="w-4 h-4 mr-1" />
              <span className="text-xs font-semibold">{langNames[lang]}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLang("en")} data-testid="qr-lang-en">🇬🇧 English</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLang("it")} data-testid="qr-lang-it">🇮🇹 Italiano</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLang("es")} data-testid="qr-lang-es">🇪🇸 Español</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end pb-8 pt-24">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
        </div>

        {/* Glow orbs */}
        <div className="absolute top-40 -left-20 w-72 h-72 bg-coral/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 -right-24 w-80 h-80 bg-ocean/40 rounded-full blur-3xl" />

        <div className="relative z-10 w-full max-w-md mx-auto px-6">
          {hotelName && (
            <div
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 animate-fade-up"
              data-testid="qr-hotel-badge"
            >
              <Sparkles className="w-3 h-3 text-coral" /> {t("qrWelcomeAt")} {decodeURIComponent(hotelName)}
            </div>
          )}
          <div
            className="animate-fade-up"
            style={{ animationDelay: "60ms" }}
          >
            <div className="inline-flex items-center gap-2 bg-coral/20 text-coral border border-coral/30 backdrop-blur-md px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4">
              <QrCode className="w-3 h-3" /> {t("qrEyebrow")}
            </div>
            <h1
              className="font-heading text-5xl sm:text-6xl font-extrabold leading-[0.95] tracking-tight"
              data-testid="qr-headline"
            >
              <span className="block">{t("qrHeadline1")}</span>
              <span className="block">{t("qrHeadline2")}</span>
              <span className="block text-coral">{t("qrHeadline3")}</span>
            </h1>
            <p className="mt-5 text-white/80 text-base leading-relaxed max-w-sm">
              {t("qrSubtitle")}
            </p>
          </div>

          <div
            className="mt-8 flex flex-col gap-3 animate-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            <Button
              onClick={() => nav("/restaurants")}
              className="btn-coral h-14 text-base font-bold shadow-2xl shadow-coral/40"
              data-testid="qr-cta-order"
            >
              {t("qrCtaOrder")}
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <button
              onClick={() => nav("/how-it-works")}
              className="text-white/70 hover:text-white text-sm underline underline-offset-4"
              data-testid="qr-cta-how"
            >
              {t("qrCtaHow")}
            </button>
          </div>

          {/* Trust stat */}
          <div
            className="mt-10 flex items-center gap-4 text-xs text-white/60 animate-fade-up"
            style={{ animationDelay: "260ms" }}
          >
            <div className="flex -space-x-2">
              {["1615540046913-40f49b1185a4", "1568901346375-23c9450c58cd", "1585937421612-70a008356fbe"].map((id) => (
                <img
                  key={id}
                  src={`https://images.unsplash.com/photo-${id}?w=80&q=70`}
                  alt=""
                  className="w-7 h-7 rounded-full border-2 border-navy object-cover"
                />
              ))}
            </div>
            <span>{t("qrTrust")}</span>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-md mx-auto px-6 py-10">
        <div className="grid grid-cols-2 gap-3">
          {perks.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.key}
                className="p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`qr-perk-${i}`}
              >
                <div className="w-9 h-9 rounded-xl bg-coral/20 text-coral flex items-center justify-center mb-2">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="font-heading font-bold text-sm">{t(`${p.key}Title`)}</div>
                <div className="text-xs text-white/60 mt-0.5">{t(`${p.key}Desc`)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS - 3 steps */}
      <section className="max-w-md mx-auto px-6 py-10">
        <h2 className="font-heading text-3xl font-extrabold mb-6">
          {t("qrHowTitle")}
        </h2>
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="flex gap-4 items-start p-4 rounded-2xl bg-white/5 border border-white/10"
              data-testid={`qr-step-${n}`}
            >
              <div className="w-10 h-10 shrink-0 rounded-full bg-coral text-white font-heading font-extrabold flex items-center justify-center">
                {n}
              </div>
              <div>
                <div className="font-heading font-bold text-base">{t(`qrStep${n}Title`)}</div>
                <div className="text-sm text-white/60 mt-0.5">{t(`qrStep${n}Desc`)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED RESTAURANTS TEASER */}
      {restaurants.length > 0 && (
        <section className="max-w-md mx-auto px-6 py-10">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-heading text-2xl font-extrabold">
              {t("qrTasteTitle")}
            </h2>
            <Link
              to="/restaurants"
              className="text-xs text-coral font-semibold flex items-center gap-1"
              data-testid="qr-see-all"
            >
              {t("viewAll")} <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {restaurants.map((r) => (
              <Link
                key={r.id}
                to={`/restaurant/${r.slug}`}
                className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-coral transition-all"
                data-testid={`qr-restaurant-${r.slug}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={r.cover_image}
                    alt={r.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <div className="font-heading font-bold text-sm truncate">{r.name}</div>
                  <div className="text-[11px] text-white/50 mt-0.5 flex items-center gap-1">
                    <Utensils className="w-3 h-3" /> €{r.delivery_fee?.toFixed(2)} · {r.delivery_time}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* STICKY BOTTOM CTA */}
      <div className="sticky bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-navy via-navy to-transparent pt-6 pb-4 px-6">
        <div className="max-w-md mx-auto">
          <Button
            onClick={() => nav("/restaurants")}
            className="btn-coral h-14 w-full text-base font-bold shadow-2xl shadow-coral/40"
            data-testid="qr-sticky-cta"
          >
            {t("qrCtaOrder")}
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
          <div className="text-center text-[11px] text-white/40 mt-3 flex items-center justify-center gap-1">
            <Wifi className="w-3 h-3" /> {t("qrFooter")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRLanding;
